/**
 *  ajax统一请求方法
 *  @param { String } path 请求路由url 允许使用动态匹配
 *  @example request('/auth/user/:id/')
 *  @param { Object } options 请求参数
 *  @param { String } options.method 请求方式
 *  @param { Object } options.params 请求携带参数，包含动态匹配的参数
 *  @return { Object } 返回axios promise对象
 *  @todo headers 参数配置
 */

import { Message } from 'element-ui';
import axios from 'axios';
// import store from '../store';
import { _cloneDeep, _isArray, _isObject, _isEmpty, Cookie } from '../../utils';
import envConfig from '@/config';

const baseURL = envConfig[`VUE_APP_${envConfig.env.toUpperCase()}_BASE_URL`];
const authURL = envConfig[`VUE_APP_${envConfig.env.toUpperCase()}_AUTH_URL`];

function queryJoin(url, key, value) {
  if (url.indexOf('?') > -1) {
    url += '&';
  } else {
    url += '?';
  }
  url += `${key}=${encodeURIComponent(value)}`;
  return url;
}

function paramsToQueryString(url, params) {
  // 目前只转换以下值类型：Array, Number, String
  if (!_isObject(params)) {
    throw new Error('params must Object!!');
  }
  if (_isObject(params) && !_isEmpty(params)) {
    for (const key in params) {
      if (_isArray(params[key])) {
        params[key].forEach((val) => { // eslint-disable-line
          url = queryJoin(url, key, val);
        });
      } else {
        url = queryJoin(url, key, params[key]);
      }
    }
  }
  return url;
}

function replaceQuery(path, options) {
  // 动态匹配参数替换
  let url = path
    .replace(/:(.*?)(\/|\?| )/g, (a, b, c) => {
      const value = options.params[b];
      delete options.params[b];
      return `${value}${c}`;
    })
    .trim();
  if (options.method === 'GET') {
    // 如果是GET请求，将对应参数转化为query params
    if (options.params) {
      url = paramsToQueryString(url, options.params);
    }
  }
  return url;
}

axios.interceptors.request.use(
  (config) => {
    const ticket = Cookie.getCookie('localTicket');
    if (ticket) {
      config.headers.Authorization = `Bearer ${ticket}`;
    }
    return config;
  },
  (error) => {
    throw new Error(error);
  }
);

axios.interceptors.response.use(
  async (response) => {
    const res = response.data;
    // 拦截数据导出统一提示
    if (res instanceof Blob) {
      if (res.type === 'application/octet-stream' || res.type === 'application/ms-excel') {
        const origin = response.headers['content-disposition'];
        const originFilename = origin.split('; ').filter((ele) => ele.indexOf('filename') !== -1);
        let filename = '';
        if (originFilename && originFilename.length > 0) {
          // filename = new String(originFilename[0].split('=')[1].replace(/"/g, '').getBytes('ISO-8859-1'), 'UTF-8');
          filename = originFilename[0].split('=')[1].replace(/"/g, '');
        }
        Message({
          message: '数据导出成功!',
          type: 'success',
          duration: 3000,
        });
        return {
          blob: response.data,
          filename,
        };
      }

      // 默认导出错误提示
      let exportErrorMsg = '导出失败';

      // 导出失败，转换为JSON
      const fr = new FileReader();
      fr.readAsText(res, 'utf-8');
      fr.onload = function onload() {
        try {
          exportErrorMsg = JSON.parse(this.result).msg;
        } catch (e) {
          console.log(e);
          throw e;
        } finally {
          Message.error({
            message: exportErrorMsg,
            duration: 5,
          });
        }
      };
      fr.onerror = function onerror() {
        Message.error({
          message: exportErrorMsg,
          duration: 5,
        });
      };

      return Promise.reject();
    }
    // 目前后端组规范将所有请求统一返回200，以code字段表示真实http状态码
    // console.log(response.data)
    const { code, msg } = response.data;
    // warning 智能云后台默认成功请求时返回code:0  权限管理系统则返回code:200  可以用response.request.url区分, 现在先偷懒
    if (code !== 0) {
      Message({
        type: 'error',
        message: msg,
        duration: 5,
      });
      if (code === 401) {
        /**
         * 后台ticket过期之后，请求登出接口验证ticket不正确时会返回401未登录
         * 前端接到401之后又会请求登出，陷入僵局
         * 所以，后台返回401时，默认ticket已经过期不再存在，前端不调用登出接口
         * 2018-6-28 10:27:14
         */
        // await store.dispatch('user/LogOut', 'local');
        // window.location.href = `${apiConfig[apiConfig.env].authURL}/oauth/logout`;
        window.location.href = `${authURL}/oauth/login?redirect=${window.location.protocol}//${window.location.host}`;
      }
      return Promise.reject(response);
    }
    return response.data;
    // return response;
  },
  (error) => {
    console.log(error);
    // debugger;
    Message({
      type: 'error',
      message: '操作失败',
      duration: 3,
    });
    return Promise.reject(error);
  }
);

export default (
  path,
  options = {
    method: 'GET',
  }
) => {
  // 由于引用数据类型按共享传递，进行配置项深拷贝
  const opt = _cloneDeep(options);
  opt.method = opt.method.toUpperCase();
  // let baseURL = ''
  const axiosConfig = {
    url: replaceQuery(path, opt),
    method: opt.method,
    data: opt.params,
    // transformRequest: [
    //     function (data) {
    //       let ret = ''
    //       for (let it in data) {
    //         ret += encodeURIComponent(it) + '=' + encodeURIComponent(data[ it ]) + '&'
    //       }
    //       return ret
    //     }
    // ],
    baseURL,
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json; charset=UTF-8',
    },
    // 'Content-Type': application/json   x-www-form-urlencoded
    // 请求超时时间
    timeout: 60000,
  };
  // 上传文件需要
  if (options.headers) {
    axiosConfig.headers['Content-Type'] = options.headers['Content-Type'];
  }
  // 下载文件需要
  if (options.responseType) {
    axiosConfig.responseType = options.responseType;
  }
  return axios(axiosConfig);
};
